import type { ComponentProps } from "react";

import type { OrderEntity } from "../model";

import { Animateness, formatCurrency, Tk, useTranslation2 } from "~/shared/i18n";
import { COLON, EMPTY } from "~/shared/str";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "~/shared/ui/table";

type Props = ComponentProps<typeof Table> & Pick<OrderEntity.Schema, "services">;

function OrderedServicesTable({ services, ...props }: Props) {
  const {
    postproc: { tc },
  } = useTranslation2();

  return (
    <Table {...props}>
      <TableHeader className="bg-gray-50">
        <TableRow>
          {COLUMN_HEADERS.map((value) => (
            <TableHead key={value}>
              {value === Tk.NAME
                ? tc(value, {
                    context: Animateness.inanimate,
                  })
                : tc(value)}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {services.map(({ id, name, price, quantity }) => {
          return (
            <TableRow key={id}>
              <TableCell>{name}</TableCell>
              <TableCell>{quantity}</TableCell>
              <TableCell>{formatCurrency(price)}</TableCell>
              <TableCell>{formatCurrency(price * quantity)}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
      <TableFooter className="bg-gray-50">
        <TableRow>
          <TableCell colSpan={COLUMN_HEADERS.length - 1} className="text-end">
            {[tc(Tk.TOTAL), COLON].join(EMPTY)}
          </TableCell>
          <TableCell className="font-medium">
            {formatCurrency(
              services.reduce((acc, { price, quantity }) => acc + price * quantity, 0),
            )}
          </TableCell>
        </TableRow>
      </TableFooter>
    </Table>
  );
}

const COLUMN_HEADERS = [Tk.NAME, Tk.QUANTITY, Tk.PRICE, Tk.SUM];

export { OrderedServicesTable };
